import { useEffect } from 'react';
import { type QueryKey, useQuery, useQueryClient } from '@tanstack/react-query';
import { Timestamp } from '../../services/Timestamp';
import { apiClient } from '../../services/ApiClient';
import { ApiEndpoints } from '../../constants/api-constants';
import { Queries, QueryCacheConfig } from '../../constants/query-constants';
import { type LiveDataViewModel, Mode } from '../../types';

export const usePollLiveData = (operation: string, mode: Mode): { data: LiveDataViewModel; isLoading: boolean } => {
  const queryClient = useQueryClient();
  const queryKey: QueryKey = [Queries.LiveData, operation];

  const getLiveData = async (): Promise<LiveDataViewModel> => {
    return apiClient.get<LiveDataViewModel>(ApiEndpoints.liveview.getLiveviewData(operation));
  };

  const { data, isLoading } = useQuery<LiveDataViewModel>({
    ...QueryCacheConfig[Queries.LiveData],
    queryKey,
    queryFn: getLiveData,
    enabled: mode === Mode.Liveview && !!operation,
  });

  useEffect(() => {
    if (mode !== Mode.Liveview || !operation) {
      return;
    }

    const intervalId = setInterval(() => {
      void queryClient.invalidateQueries({ queryKey });
    }, Timestamp.fromSeconds(10).valueInMilliseconds);

    return (): void => {
      clearInterval(intervalId);
    };
  }, [mode, operation]);

  return { data, isLoading };
};
